import React from 'react';
import {
  Badge,
  Card,
  CardContent,
  Box,
  TextField,
  MenuItem,
  IconButton,
  Typography,
} from '@mui/material';
import { Delete, AttachFile } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export default function ExpenseRowCard({
  row,
  index,
  categories,
  accounts,
  vats,
  onUpdate,
  onDelete,
  onAttach,
  canDelete,
  readOnly,
}) {
  const { t } = useTranslation();

  const handleChange = (field, value) => {
    const updated = { ...row, [field]: value };

    // When category changes, set unit_price from category
    if (field === 'category_id') {
      const cat = categories.find(c => c.id === value);
      if (cat) {
        updated.unit_price = parseFloat(cat.unit_price) || 0;
        if (updated.unit_price > 0) {
          updated.amount = parseFloat(((updated.quantity || 1) * updated.unit_price).toFixed(2));
          const tax = updated.tax || 0;
          updated.vat_amount = parseFloat((updated.amount * tax / 100).toFixed(2));
          updated.total = parseFloat((updated.amount + updated.vat_amount).toFixed(2));
          onUpdate(updated);
          return;
        }
      } else {
        updated.unit_price = 0;
      }
    }

    // When quantity changes and unit_price > 0, recalculate amount
    if (field === 'quantity' && updated.unit_price > 0) {
      updated.amount = parseFloat(((value || 1) * updated.unit_price).toFixed(2));
      const tax = updated.tax || 0;
      updated.vat_amount = parseFloat((updated.amount * tax / 100).toFixed(2));
      updated.total = parseFloat((updated.amount + updated.vat_amount).toFixed(2));
      onUpdate(updated);
      return;
    }

    // When VAT changes, update tax and recalculate
    if (field === 'vat_id') {
      const vat = vats.find(v => v.id === value);
      if (vat) {
        updated.tax = parseFloat(vat.percent);
        updated.vat_code = vat.code;
      } else {
        updated.tax = 0;
        updated.vat_code = '';
      }
      const tax = updated.tax || 0;
      updated.vat_amount = parseFloat(((updated.amount || 0) * tax / 100).toFixed(2));
      updated.total = parseFloat(((updated.amount || 0) + updated.vat_amount).toFixed(2));
      onUpdate(updated);
      return;
    }

    // When account changes, apply account's default VAT
    if (field === 'account_id') {
      const acc = accounts.find(a => a.id === value);
      if (acc) {
        updated.account_code = acc.code;
        updated.account_name = acc.name;
        if (acc.vat) {
          const accountVat = vats.find(v => v.id === acc.vat);
          if (accountVat) {
            updated.vat_id = accountVat.id;
            updated.tax = parseFloat(accountVat.percent);
            updated.vat_code = accountVat.code;
            const tax = updated.tax || 0;
            updated.vat_amount = parseFloat(((updated.amount || 0) * tax / 100).toFixed(2));
            updated.total = parseFloat(((updated.amount || 0) + updated.vat_amount).toFixed(2));
          }
        }
      } else {
        updated.account_code = '';
        updated.account_name = '';
      }
    }

    // When amount changes, recalculate vat_amount and total
    if (field === 'amount') {
      const tax = updated.tax || 0;
      updated.vat_amount = parseFloat(((parseFloat(value) || 0) * tax / 100).toFixed(2));
      updated.total = parseFloat(((parseFloat(value) || 0) + updated.vat_amount).toFixed(2));
    }

    onUpdate(updated);
  };

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        {/* Header: row number + attachment badge + delete */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            #{index + 1}
          </Typography>
          <Box>
            {onAttach && (
              <IconButton size="small" onClick={() => onAttach(row)}>
                <Badge badgeContent={row.attachment_count || 0} color="primary" overlap="circular">
                  <AttachFile fontSize="small" />
                </Badge>
              </IconButton>
            )}
            {canDelete && !readOnly && (
              <IconButton size="small" onClick={() => onDelete(row.id)} color="error">
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Category select */}
        <TextField
          select
          fullWidth
          label={t('expenses.row.category')}
          value={row.category_id || ''}
          onChange={(e) => handleChange('category_id', e.target.value)}
          size="small"
          sx={{ mb: 1.5 }}
          disabled={readOnly}
        >
          <MenuItem value="">-</MenuItem>
          {categories.map(cat => (
            <MenuItem key={cat.id} value={cat.id}>
              {cat.code} - {cat.name}
            </MenuItem>
          ))}
        </TextField>

        {/* Account select */}
        <TextField
          select
          fullWidth
          label={t('expenses.row.account')}
          value={row.account_id || ''}
          onChange={(e) => handleChange('account_id', e.target.value)}
          size="small"
          sx={{ mb: 1.5 }}
          disabled={readOnly}
        >
          <MenuItem value="">-</MenuItem>
          {(accounts || []).map(acc => (
            <MenuItem key={acc.id} value={acc.id}>
              {acc.code} - {acc.name}
            </MenuItem>
          ))}
        </TextField>

        {/* Description */}
        <TextField
          fullWidth
          label={t('expenses.row.description')}
          value={row.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          size="small"
          sx={{ mb: 1.5 }}
          disabled={readOnly}
        />

        {/* Quantity, Amount, VAT, Total row */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          {row.unit_price > 0 && (
            <TextField
              label={t('expenses.row.quantity')}
              type="number"
              value={row.quantity ?? 1}
              onChange={(e) => handleChange('quantity', parseFloat(e.target.value) || 0)}
              size="small"
              sx={{ flex: 1, minWidth: 80 }}
              disabled={readOnly}
              inputProps={{ inputMode: 'decimal' }}
            />
          )}
          <TextField
            label={t('expenses.row.amount')}
            type="number"
            value={row.amount ? parseFloat(row.amount).toFixed(2) : ''}
            onChange={(e) => handleChange('amount', parseFloat(e.target.value) || 0)}
            size="small"
            sx={{ flex: 1, minWidth: 90 }}
            disabled={readOnly || row.unit_price > 0}
            inputProps={{ inputMode: 'decimal', step: '0.01' }}
          />
          <TextField
            select
            label={t('expenses.row.vat')}
            value={row.vat_id || ''}
            onChange={(e) => handleChange('vat_id', e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 100 }}
            disabled={readOnly}
          >
            <MenuItem value="">-</MenuItem>
            {vats.map(v => (
              <MenuItem key={v.id} value={v.id}>
                {v.percent}% ({v.code})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('expenses.row.total')}
            type="number"
            value={row.total ? parseFloat(row.total).toFixed(2) : ''}
            size="small"
            sx={{ flex: 1, minWidth: 90 }}
            disabled
            inputProps={{ inputMode: 'decimal' }}
          />
        </Box>

        {/* Paid By select */}
        <TextField
          select
          fullWidth
          label={t('expenses.row.paid_by')}
          value={row.paid_by ?? 0}
          onChange={(e) => handleChange('paid_by', parseInt(e.target.value))}
          size="small"
          disabled={readOnly}
        >
          <MenuItem value={0}>{t('expenses.paid_by_employee')}</MenuItem>
          <MenuItem value={1}>{t('expenses.paid_by_company')}</MenuItem>
        </TextField>
      </CardContent>
    </Card>
  );
}
